import { useLayoutEffect, useRef } from "react";

import {
  interpolateSummary,
  prepareSummaryMorph,
  readSummaryContour,
  summaryContourPaths,
  type SummaryContour,
} from "./summary-motion";

const SUMMARY_MOTION_MS = 220;
type Paths = { fill: string; stroke: string } | null;

/** Animate only SVG attributes; React and density counting stay outside RAF. */
export function useSummaryMotion(paths: Paths, baseY: number, layoutKey: string, revision?: object) {
  const fillRef = useRef<SVGPathElement>(null);
  const strokeRef = useRef<SVGPathElement>(null);
  const current = useRef<{
    contour: SummaryContour;
    layoutKey: string;
    revision?: object;
    settledStroke?: string;
  } | null>(null);
  const fill = paths?.fill;
  const stroke = paths?.stroke;

  useLayoutEffect(() => {
    if (!fill || !stroke || !fillRef.current || !strokeRef.current) {
      current.current = null;
      return;
    }
    const target = readSummaryContour(stroke);
    const previous = current.current;
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    let frame: number | undefined;
    const paint = (next: Exclude<Paths, null>) => {
      fillRef.current?.setAttribute("d", next.fill);
      strokeRef.current?.setAttribute("d", next.stroke);
    };
    const finish = () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = undefined;
      current.current = { contour: target, layoutKey, revision, settledStroke: stroke };
      paint({ fill, stroke });
    };
    if (
      !previous ||
      previous.layoutKey !== layoutKey ||
      previous.revision !== revision ||
      previous.settledStroke === stroke ||
      media?.matches
    ) {
      finish();
      return;
    }
    const morph = prepareSummaryMorph(previous.contour, target);
    const startedAt = performance.now();
    // Restore the last painted geometry before the browser paints React's new d.
    paint(summaryContourPaths(previous.contour, baseY));
    const tick = (now: number) => {
      const progress = Math.min(1, Math.max(0, (now - startedAt) / SUMMARY_MOTION_MS));
      if (progress >= 1) {
        finish();
        return;
      }
      const contour = interpolateSummary(morph, 1 - (1 - progress) ** 3);
      current.current = { contour, layoutKey, revision };
      paint(summaryContourPaths(contour, baseY));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    const onMotionChange = () => {
      if (media?.matches) finish();
    };
    media?.addEventListener("change", onMotionChange);
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      media?.removeEventListener("change", onMotionChange);
    };
  }, [fill, stroke, baseY, layoutKey, revision]);

  return { fillRef, strokeRef };
}
